import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, Form, Typography } from 'antd';
import { memo, useCallback, useMemo, useRef } from 'react';
import { jsonSchemaToZod } from './validators';
import { FieldFactory } from './FieldFactory';
import { renderMarkedLabel } from './ClauseText';
import { applyCopyRules, buildFieldMap, collectCopyRules, isFieldVisible, lockedFieldIds, } from './field-links';
const FieldRow = memo(function FieldRow({ field, value, onChange, disabled, error, uploadHandlers, }) {
    return (_jsx(Form.Item
    // checkbox 自帶勾選框文字，不再重複顯示 Form.Item 標題（序66：「與戶籍地址相同」直接一個勾選框）
    // 標籤含（★★）時標紅（序53：查核表標籤「註：（★★）見證評鑑缺失之增修條文為必查核重點」）
    , { 
        // checkbox 自帶勾選框文字，不再重複顯示 Form.Item 標題（序66：「與戶籍地址相同」直接一個勾選框）
        // 標籤含（★★）時標紅（序53：查核表標籤「註：（★★）見證評鑑缺失之增修條文為必查核重點」）
        label: field.type === 'checkbox' ? undefined : renderMarkedLabel(field.label), required: 'required' in field && field.required, validateStatus: error ? 'error' : undefined, help: error, extra: field.help, children: _jsx(FieldFactory, { field: field, value: value, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }) }));
});
/** 只讀說明文字（section.description / footnote）：保留換行 */
function SectionNote({ text }) {
    return (_jsx(Typography.Paragraph, { type: "secondary", style: { whiteSpace: 'pre-wrap', marginBottom: 12, fontSize: 13 }, children: text }));
}
/**
 * 依 `inline` 把欄位分列：inline 欄位接在上一個欄位同一列（序69：集團驗證，農戶數）。
 * 回傳二維陣列，每個子陣列為同一列的欄位。
 */
function groupInlineFields(fields) {
    const rows = [];
    for (const f of fields) {
        const last = rows[rows.length - 1];
        if (f.inline && last)
            last.push(f);
        else
            rows.push([f]);
    }
    return rows;
}
const SectionCard = memo(function SectionCard({ section, value, fieldOnChange, disabled, errors, uploadHandlers, lockedKey, hiddenKey, }) {
    const locked = new Set(lockedKey ? lockedKey.split(',') : []);
    const hidden = new Set(hiddenKey ? hiddenKey.split(',') : []);
    /** 渲染單一欄位（條件隱藏者回 null） */
    const renderField = (field) => !hidden.has(field.id) ? (_jsx(FieldRow, { field: field, value: value[field.id], onChange: fieldOnChange.get(field.id), disabled: disabled || locked.has(field.id), error: errors?.[field.id], uploadHandlers: uploadHandlers }, field.id)) : null;
    return (_jsxs(Card, { title: section.title, children: [section.description && _jsx(SectionNote, { text: section.description }), _jsx(Form, { layout: "vertical", children: groupInlineFields(section.fields).map((row) => row.length === 1 ? (renderField(row[0])) : (_jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }, children: row.map(renderField) }, row.map((f) => f.id).join('|')))) }), section.footnote && _jsx(SectionNote, { text: section.footnote })] }));
}, 
// 仅当本 section 自己欄位的值（或其它穩定 props）變化時才重渲染；否則整塊 bail。
(prev, next) => {
    if (prev.section !== next.section ||
        prev.disabled !== next.disabled ||
        prev.uploadHandlers !== next.uploadHandlers ||
        prev.errors !== next.errors ||
        prev.fieldOnChange !== next.fieldOnChange ||
        prev.lockedKey !== next.lockedKey ||
        prev.hiddenKey !== next.hiddenKey) {
        return false;
    }
    for (const field of next.section.fields) {
        if (prev.value[field.id] !== next.value[field.id])
            return false;
        // showWhen（含跨 section / 多層祖先）依賴變化已由 hiddenKey 快照涵蓋
    }
    return true;
});
/**
 * 表單渲染器：根據 FormSchema 動態渲染所有 section 和 field。
 * 使用 antd Card + Form.Item 包裝，支援錯誤顯示和禁用狀態。
 * 實際提交時呼叫 jsonSchemaToZod(schema).parse(value) 做完整校驗。
 */
export function Renderer({ schema, value, onChange, disabled, errors, uploadHandlers }) {
    // 預先驗證 schema 結構合法性，確保後續渲染時 schema 有效
    // 完整的資料校驗在提交時進行
    useMemo(() => jsonSchemaToZod(schema), [schema]);
    // ── 穩定的欄位級更新回調 ──
    // value/onChange 透過 ref 持有最新值，使 updateField 引用恆定（useCallback 空依賴）；
    // 否則每次 keystroke 換 value → 每個欄位的 onChange 閉包都換引用 → 打穿 FieldFactory 的 memo，
    // 導致在任一欄位輸入時整張表單的所有欄位（含上百條 checklist 控件）全部重渲染 → 卡頓。
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    /** 「勾選即複製」規則（序66：與戶籍地址相同；顯式 copy 宣告 + mailing_same_as_registered 慣例） */
    const copyRules = useMemo(() => collectCopyRules(schema), [schema]);
    const copyRulesRef = useRef(copyRules);
    copyRulesRef.current = copyRules;
    /**
     * 更新單一欄位的值，合併到整體 FormInstanceData（讀 ref 中的最新 value，引用恆定）；
     * 合併後套用複製規則（勾選時目標欄位同步來源值）。
     */
    const updateField = useCallback((id, next) => {
        // 傳入變更欄位 id：只有勾選框或來源欄位被編輯時才觸發複製，避免其他欄位輸入時覆寫目標
        onChangeRef.current(applyCopyRules(copyRulesRef.current, { ...valueRef.current, [id]: next }, id));
    }, []);
    /** 當前被鎖定的目標欄位（字串化供 SectionCard memo 比較） */
    const lockedKey = [...lockedFieldIds(copyRules, value)].sort().join(',');
    /** 欄位 id → 定義對照表（遞移可見性判斷用） */
    const fieldMap = useMemo(() => buildFieldMap(schema), [schema]);
    /**
     * 當前被隱藏的欄位 id 快照（遞移判斷：依據欄位被隱藏時本欄位亦隱藏）。
     * 字串化供 SectionCard memo 比較；只掃描帶 showWhen 的欄位，成本可忽略。
     */
    const hiddenKey = useMemo(() => {
        const ids = [];
        for (const f of fieldMap.values()) {
            if (f.showWhen && !isFieldVisible(f, value, fieldMap))
                ids.push(f.id);
        }
        return ids.sort().join(',');
    }, [fieldMap, value]);
    /**
     * 為每個欄位預生成「穩定」的單參 onChange（id 取自 schema，渲染間不變）。
     * 只要 schema 不變，同一 field.id 永遠拿到同一函式引用 → 配合 React.memo(FieldFactory)，
     * 未編輯的欄位在他欄輸入時直接 bail-out，不重渲染。
     */
    const fieldOnChange = useMemo(() => {
        const map = new Map();
        for (const section of schema.sections ?? []) {
            for (const field of section.fields) {
                const id = field.id;
                map.set(id, (next) => updateField(id, next));
            }
        }
        return map;
    }, [schema, updateField]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(Typography.Title, { level: 3, children: schema.title }), schema.description && _jsx(Typography.Paragraph, { children: schema.description }), (schema.sections ?? []).map((section) => (_jsx(SectionCard, { section: section, value: value, fieldOnChange: fieldOnChange, disabled: disabled, errors: errors, uploadHandlers: uploadHandlers, lockedKey: lockedKey, hiddenKey: hiddenKey }, section.id)))] }));
}
//# sourceMappingURL=Renderer.js.map